import * as Icons from '@ant-design/icons';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Flex,
  Form,
  Table,
  Tag,
  Tooltip,
  Upload
} from 'antd';
import { CreateLayout } from 'components/layout/layout/CreateLayout';
import { DeleteOutlined, RollbackOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { PageHeader } from 'components/professor_area/PageHeader';
import { UserRow } from 'interfaces/bulk_users';
import { parseFile } from 'services/bulk-users';
import { useForm } from '@refinedev/antd';
import { useNavigation } from '@refinedev/core';
import { useState } from 'react';
import { getFullNameValidationError, isValidFullName } from 'utils/validators';

interface FileTableProps {
  userList: UserRow[];
  removedIndices: Set<number>;
  onRemove: (index: number) => void;
  onRestore: (index: number) => void;
}

const FileTable = ({
  userList,
  removedIndices,
  onRemove,
  onRestore
}: FileTableProps) => (
  <Table
    dataSource={userList.map((item, index) => ({ ...item, index }))}
    rowKey={record => record.index}
    columns={[
      {
        title: 'Nome Completo',
        key: 'name',
        dataIndex: 'name',
        render: (text: string, record: UserRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);
          const errors = getRowErrors(record);
          return renderCell(text, isRemoved || rowInvalid, errors.name);
        }
      },
      {
        title: 'Email',
        key: 'email',
        dataIndex: 'email',
        render: (text: string, record: UserRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);
          const errors = getRowErrors(record);
          return renderCell(text, isRemoved || rowInvalid, errors.email);
        }
      },
      {
        title: 'Senha',
        key: 'password',
        dataIndex: 'password',
        render: (text: string, record: UserRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);
          const errors = getRowErrors(record);
          return renderCell(text, isRemoved || rowInvalid, errors.password);
        }
      },
      {
        title: 'Telefone',
        key: 'phone_number',
        dataIndex: 'phone_number',
        render: (text: string, record: UserRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);
          return renderCell(text, isRemoved || rowInvalid);
        }
      },
      {
        title: 'Status',
        key: 'status',
        width: 110,
        align: 'center',
        render: (_: unknown, record: UserRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);

          if (isRemoved)
            return (
              <Icons.CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
            );
          if (rowInvalid) return <Tag color="warning">Inválido</Tag>;
          return <Icons.CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />;
        }
      },
      {
        title: 'Ações',
        key: 'actions',
        width: 140,
        render: (_: unknown, record: UserRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);

          if (rowInvalid) return null;

          return isRemoved ? (
            <Button
              type="link"
              size="small"
              icon={<RollbackOutlined />}
              onClick={() => onRestore(record.index)}
            >
              Voltar
            </Button>
          ) : (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onRemove(record.index)}
            >
              Excluir
            </Button>
          );
        }
      }
    ]}
    size="small"
    pagination={{ pageSize: 20 }}
  />
);

const checkField = (field: string) => field?.length && field.length > 0;

const isValidEmail = (email: string) =>
  checkField(email) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isValidPassword = (password: string) =>
  checkField(password) && password.length >= 8;

const isRowValid = (row: UserRow) =>
  isValidFullName(row.name) && isValidEmail(row.email) && isValidPassword(row.password);

const getRowErrors = (row: UserRow) => ({
  name: getFullNameValidationError(row.name, 'Nome Completo'),
  email: !isValidEmail(row.email) ? 'E-mail inválido' : undefined,
  password: !isValidPassword(row.password)
    ? 'Senha deve ter ao menos 8 caracteres'
    : undefined
});

const renderCell = (
  text: string,
  isStrikethrough: boolean,
  errorMsg?: string
) => {
  const content = (
    <span
      style={{
        textDecoration: isStrikethrough ? 'line-through' : 'none',
        opacity: isStrikethrough ? 0.6 : 1,
        color: errorMsg ? '#ff4d4f' : undefined
      }}
    >
      {text || '—'}
    </span>
  );

  return errorMsg ? <Tooltip title={errorMsg}>{content}</Tooltip> : content;
};

export const BulkUserCreate = () => {
  const router = useNavigation();

  const { formProps, saveButtonProps, formLoading } = useForm({
    redirect: false,
    successNotification: () => ({
      type: 'success',
      message: 'Importação concluída com sucesso',
      description: 'Os alunos foram importados.'
    }),
    onMutationSuccess: () => {
      router.list('users');
    }
  });

  const [userList, setUserList] = useState<UserRow[]>([]);
  const [removedIndices, setRemovedIndices] = useState<Set<number>>(new Set());

  const activeUserList = userList.filter(
    (row, index) => !removedIndices.has(index) && isRowValid(row)
  );

  const hasInvalidRows = userList.some(
    (row, index) => !removedIndices.has(index) && !isRowValid(row)
  );

  const valid = activeUserList.length > 0;

  const handleRemove = (index: number) => {
    setRemovedIndices(prev => new Set(prev).add(index));
  };

  const handleRestore = (index: number) => {
    setRemovedIndices(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  };

  // eslint-disable-next-line @typescript-eslint/ban-types
  const onFinish = (values: {}) => {
    formProps.onFinish!({ ...values, bulk: activeUserList });
  };

  return (
    <CreateLayout
      breadcrumb={
        <Breadcrumb>
          <Breadcrumb.Item>
            <Flex gap={4}>
              <Icons.TeamOutlined />
              Alunos e docentes
            </Flex>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/students_teachers/users">Alunos</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Adicionar alunos em lote</Breadcrumb.Item>
        </Breadcrumb>
      }
      saveButtonProps={{
        ...saveButtonProps,
        disabled: !valid || activeUserList.length === 0,
        loading: formLoading
      }}
    >
      <PageHeader title="Adicionar alunos em lote" icon={Icons.TeamOutlined} />

      <Card>
        <Alert
          type="info"
          showIcon
          message="Formato do arquivo CSV"
          description={
            <div>
              <p>
                O arquivo deve ser um CSV (valores separados por vírgula) com as
                seguintes colunas:
              </p>
              <ul style={{ marginLeft: 20, marginTop: 8, marginBottom: 8 }}>
                <li>
                  <strong>Nome Completo</strong> - Nome completo do aluno
                </li>
                <li>
                  <strong>Email</strong> - E-mail do aluno
                </li>
                <li>
                  <strong>Senha</strong> - Senha de acesso
                </li>
                <li>
                  <strong>Telefone</strong> - Telefone do aluno (opcional)
                </li>
              </ul>
              <p style={{ marginTop: 8 }}>
                <strong>Exemplo:</strong>
                <br />
                <code style={{ fontSize: 12 }}>
                  Nome Completo,Email,Senha,Telefone
                  <br />
                  João Silva,joao@exemplo.com,senha123,11999999999
                </code>
              </p>
              <p style={{ marginTop: 8, marginBottom: 0 }}>
                <strong>Observação:</strong> Usuários já cadastrados não serão
                atualizados.
              </p>
            </div>
          }
          style={{ marginBottom: 16 }}
        />

        <Form {...formProps} layout="vertical" onFinish={onFinish}>
          <Form.Item>
            <Upload
              accept="text/csv"
              fileList={[]}
              onRemove={() => {
                setUserList([]);
                setRemovedIndices(new Set());
              }}
              beforeUpload={async newFile => {
                const rows = await parseFile(newFile);

                setUserList(rows);
                setRemovedIndices(new Set());

                return false;
              }}
            >
              <Button icon={<Icons.UploadOutlined />}>Escolher arquivo</Button>
            </Upload>
          </Form.Item>
        </Form>
        <FileTable
          userList={userList}
          removedIndices={removedIndices}
          onRemove={handleRemove}
          onRestore={handleRestore}
        />
        {hasInvalidRows && (
          <Alert
            type="warning"
            showIcon
            message="Linhas inválidas não serão importadas"
            description="Passe o mouse sobre os campos em vermelho para ver o motivo."
            style={{ marginTop: 12 }}
          />
        )}
      </Card>
    </CreateLayout>
  );
};
